import { isEqual } from 'lodash';
import * as OpenTripPlannerActions from './open-trip-planner.actions';
import { createReducer, on } from '@ngrx/store';
import {
    ExploreMapRoute,
    Itinerary,
    JourneyMapPlan,
    PlannerError,
    Route,
    RouteGroups,
    SearchRecentlyJourney,
    Stop,
    StopMarker,
    TripDetails
} from '../open-trip-planner.model';
import { DisruptionMessage, LatLng } from '@ridango/core';
import { JourneyPlannerFilter } from '@ridango/components/lib/open-trip-planner/journey-planner-filter.model';

export interface State {
    routes: Route[];
    routesGroups: RouteGroups[];
    stops: Stop[];
    location: {
        coordinates?: LatLng;
        hasError: boolean;
    };
    searchRecentlyJourneys: Array<SearchRecentlyJourney>;
    search: {
        origin?: {
            name: string;
            coordinates: LatLng;
            useCurrentLocation: boolean;
            useLocationFromMap?: boolean;
            municipalityName?: string;
            stopId?: number;
        };
        destination?: {
            name: string;
            coordinates: LatLng;
            useCurrentLocation: boolean;
            useLocationFromMap?: boolean;
            municipalityName?: string;
            stopId?: number;
        };
        filters: JourneyPlannerFilter;
        searchDateAndTime: {
            whiteListedRoutes?: Route[];
            date: string;
            time: string;
            arrive: boolean;
            disabled?: boolean;
        };
    };
    plan: {
        loading: boolean;
        error: PlannerError;
        itineraries: Itinerary[];
    };
    selectedItineraryForDetails: Itinerary;
    exploreMapStops: StopMarker[];
    exploreMapRoute: ExploreMapRoute;
    journeyMapPlan: JourneyMapPlan;
    nearbyStops: Stop[];
    nearbyStopsLoading: boolean;
    trips: TripDetails[];
    alerts: DisruptionMessage[];
}

export const FEATURE_KEY = 'OTP';

export interface OpenTripPlannerState {
    readonly [FEATURE_KEY]: State;
}

export const initialState: State = {
    routes: [],
    routesGroups: [],
    stops: [],
    location: { coordinates: null, hasError: false },
    search: null,
    searchRecentlyJourneys: [],
    plan: {
        loading: false,
        error: null,
        itineraries: []
    },
    selectedItineraryForDetails: null,
    exploreMapStops: [],
    exploreMapRoute: null,
    journeyMapPlan: null,
    nearbyStops: [],
    nearbyStopsLoading: false,
    trips: [],
    alerts: []
};

export const openTripPlannerReducer = createReducer(
    initialState,

    on(OpenTripPlannerActions.getAllRoutesSuccess, (state, { routes }) => ({
        ...state,
        routes: routes
    })),

    on(OpenTripPlannerActions.getAllRoutesGroupsSuccess, (state, { routesGroups }) => ({
        ...state,
        routesGroups: routesGroups
    })),

    on(OpenTripPlannerActions.getAllStopsSuccess, (state, { stops }) => ({
        ...state,
        stops: stops
    })),

    on(OpenTripPlannerActions.getAllAlerts, (state) => ({
        ...state,
        alertsError: null
    })),

    on(OpenTripPlannerActions.getAllAlertsSuccess, (state, { alerts }) => ({
        ...state,
        alerts: alerts
    })),

    on(OpenTripPlannerActions.getAllAlertsFailure, (state, { error }) => ({
        ...state,
        alertsError: error,
        alerts: []
    })),

    on(OpenTripPlannerActions.setCurrentLocation, (state, { coordinates, hasError }) => ({
        ...state,
        location: { coordinates: coordinates, hasError: hasError }
    })),

    on(
        OpenTripPlannerActions.setSearchOrigin,
        (state, { name, coordinates, isCurrentLocation, isLocationFromMap, municipalityName, fromStopId }) => ({
            ...state,
            search: {
                ...state.search,
                origin:
                    name !== null && coordinates !== null && isCurrentLocation !== null
                        ? {
                              name: name,
                              coordinates: coordinates,
                              useCurrentLocation: isCurrentLocation,
                              useLocationFromMap: isLocationFromMap,
                              municipalityName: municipalityName,
                              stopId: fromStopId
                          }
                        : null
            }
        })
    ),

    on(
        OpenTripPlannerActions.setSearchDestination,
        (state, { name, coordinates, isCurrentLocation, isLocationFromMap, municipalityName, toStopId }) => ({
            ...state,
            search: {
                ...state.search,
                destination:
                    name !== null && coordinates !== null && isCurrentLocation !== null
                        ? {
                              name: name,
                              coordinates: coordinates,
                              useCurrentLocation: isCurrentLocation,
                              useLocationFromMap: isLocationFromMap,
                              municipalityName: municipalityName,
                              stopId: toStopId
                          }
                        : null
            }
        })
    ),

    on(OpenTripPlannerActions.clearSearch, (state) => ({
        ...state,
        search: null
    })),

    on(OpenTripPlannerActions.setSearchDisabled, (state, { disabled }) => ({
        ...state,
        search: {
            ...state.search,
            disabled: disabled
        }
    })),

    on(OpenTripPlannerActions.getPlan, (state) => ({
        ...state,
        plan: {
            loading: true,
            error: null,
            itineraries: []
        }
    })),

    on(OpenTripPlannerActions.getPlanSuccess, (state, { itineraries }) => ({
        ...state,
        plan: {
            loading: false,
            error: null,
            itineraries: itineraries
        }
    })),

    on(OpenTripPlannerActions.getPlanError, (state, { error }) => ({
        ...state,
        plan: {
            loading: false,
            error: error,
            itineraries: []
        }
    })),

    on(OpenTripPlannerActions.setJourneyPlannerFilters, (state, { filters }) => ({
        ...state,
        search: {
            ...state.search,
            filters
        }
    })),

    on(OpenTripPlannerActions.setExploreMapStops, (state, { exploreMapStops }) => ({
        ...state,
        exploreMapStops: exploreMapStops
    })),

    on(OpenTripPlannerActions.setExploreMapRoute, (state, { exploreMapRoute }) => ({
        ...state,
        exploreMapRoute: exploreMapRoute
    })),

    on(OpenTripPlannerActions.setJourneyMapPlan, (state, { journeyMapPlan }) => ({
        ...state,
        journeyMapPlan: journeyMapPlan
    })),

    on(OpenTripPlannerActions.setSelectedItineraryForDetails, (state, { itinerary }) => ({
        ...state,
        selectedItineraryForDetails: itinerary
    })),

    on(OpenTripPlannerActions.swapSearchOriginAndDestination, (state) => ({
        ...state,
        search: {
            ...state.search,
            origin: state.search?.destination
                ? {
                      ...state.search?.destination
                  }
                : null,
            destination: state.search?.origin
                ? {
                      ...state.search?.origin
                  }
                : null
        }
    })),

    on(OpenTripPlannerActions.setSearchDateAndTime, (state, { date, time, arrive }) => ({
        ...state,
        search: {
            ...state.search,
            searchDateAndTime: {
                date: date,
                time: time,
                arrive: arrive
            }
        }
    })),

    on(OpenTripPlannerActions.addSearchRecentlyJourney, (state, payload) => ({
        ...state,
        searchRecentlyJourneys: [
            {
                origin: payload.origin,
                destination: payload.destination
            },
            ...state.searchRecentlyJourneys.filter(
                (j) =>
                    !(
                        isEqual(j.origin.coordinates, payload.origin.coordinates) &&
                        isEqual(j.destination.coordinates, payload.destination.coordinates)
                    )
            )
        ].slice(0, 3)
    })),

    on(OpenTripPlannerActions.clearSearchRecentlyJourneys, (state) => ({
        ...state,
        searchRecentlyJourneys: []
    })),

    on(OpenTripPlannerActions.setSearchRecentlyJourneys, (state, { result }) => ({
        ...state,
        searchRecentlyJourneys: result
    })),

    on(OpenTripPlannerActions.getNearbyStops, (state) => ({
        ...state,
        nearbyStopsLoading: true
    })),

    on(OpenTripPlannerActions.getNearbyStopsSuccess, (state, { nearbyStops }) => ({
        ...state,
        nearbyStops,
        nearbyStopsLoading: false
    })),

    on(OpenTripPlannerActions.getNearbyStopsError, (state) => ({
        ...state,
        nearbyStops: [],
        nearbyStopsLoading: false
    })),

    on(OpenTripPlannerActions.loadTripsByRouteId, (state) => ({
        ...state,
        loading: true,
        error: null
    })),
    on(OpenTripPlannerActions.loadTripsByRouteIdSuccess, (state, { trips }) => ({
        ...state,
        trips,
        loading: false
    })),
    on(OpenTripPlannerActions.loadTripsByRouteIdFailure, (state, { error }) => ({
        ...state,
        error,
        loading: false
    })),
    on(OpenTripPlannerActions.clearPlanResults, (state) => {
        return {
            ...state,
            plan: {
                loading: false,
                error: null,
                itineraries: []
            }
        };
    }),
);
